import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, CalendarOff, Clock, Home, Building2, AlertCircle } from 'lucide-react';
import api from '../utils/axios';
import Dropdown from '../components/Dropdown';
import { cellClass, monthOptions } from '../utils/attendanceCell';
import usePersistedState from '../hooks/usePersistedState';

const MyAttendance = () => {
  const months = useMemo(() => monthOptions(), []);
  const [month, setMonth] = usePersistedState('myAttendance.month', months[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/attendance/me?month=${month}`);
      setData(res.data.data);
    } catch (err) {
      setError('Could not load your attendance. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // Company time, from the API. Rendering in the viewer's zone made this column
  // contradict both the (late) badge and the UTC-pinned Date column beside it.
  const zone = (data && data.timezone) || 'UTC';

  // Newest first. The API returns the month ascending, which put the 1st at the
  // top and today at the very bottom - and the top row reads as "today" at a
  // glance, so the 3rd was misread as the 31st. The matrix keeps its ascending
  // left-to-right columns; this is a single-person list, where recency wins.
  const days = useMemo(() => {
    if (!data || !data.rows.length) return [];
    const row = data.rows[0];
    return data.dates.map((date) => ({ date, ...row.cells[date] })).filter((d) => d.id).reverse();
  }, [data]);

  // Mirrors the server's counting in attendanceIngest: OOO and Holiday are
  // neither present nor absent. Holiday used to fall through to the else branch
  // and inflate Present.
  const summary = useMemo(() => days.reduce((acc, d) => {
    if (d.location === 'Holiday') acc.holiday += 1;
    else if (d.location === 'OOO') acc.leave += 1;
    else if (d.location === 'Absent') acc.absent += 1;
    else {
      acc.present += 1;
      if (/\(late\)/.test(d.cell)) acc.late += 1;
    }
    return acc;
  }, {
    present: 0, late: 0, absent: 0, leave: 0, holiday: 0,
  }), [days]);

  const tiles = [
    { key: 'present', label: 'Present', value: summary.present, icon: Building2, tone: 'text-green-700' },
    { key: 'late', label: 'Late', value: summary.late, icon: Clock, tone: 'text-amber-700' },
    { key: 'absent', label: 'Absent', value: summary.absent, icon: AlertCircle, tone: 'text-red-700' },
    { key: 'leave', label: 'On leave', value: summary.leave, icon: Home, tone: 'text-gray-700' },
    { key: 'holiday', label: 'Holidays', value: summary.holiday, icon: CalendarOff, tone: 'text-indigo-700' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">My Attendance</h1>
          <p className="mt-1 text-sm text-gray-500">
            Derived from your posts in #availability. Talk to HR if something looks wrong.
          </p>
        </div>
        <div className="w-full sm:w-56">
          <label htmlFor="month" className="block text-sm font-medium text-gray-700">Month</label>
          <Dropdown id="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1">
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Dropdown>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {tiles.map(({ key, label, value, icon: Icon, tone }) => (
          <div key={key} className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">{label}</span>
              <Icon className={`h-4 w-4 ${tone}`} aria-hidden="true" />
            </div>
            <p data-testid={`summary-${key}`} className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {error && <p className="p-6 text-sm text-red-700">{error}</p>}
        {!error && loading && <p className="p-6 text-sm text-gray-500">Loading...</p>}
        {!error && !loading && days.length === 0 && (
          <div className="p-10 text-center">
            <Calendar className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
            <p className="mt-2 text-sm text-gray-500">No attendance recorded for this month.</p>
          </div>
        )}
        {!error && !loading && days.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Signed in</th>
                  <th scope="col" className="px-4 py-3 text-left font-medium text-gray-500">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {days.map((d) => (
                  <tr key={d.date}>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900">
                      {new Date(`${d.date}T00:00:00Z`).toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cellClass(d.cell)}`}>
                        {d.cell}
                      </span>
                      {d.overridden && (
                        <span className="ml-2 text-xs text-gray-500">corrected by HR</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                      {d.first_signin_at
                        ? new Date(d.first_signin_at).toLocaleTimeString('en-US', {
                          hour: '2-digit', minute: '2-digit', timeZone: zone,
                        })
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{d.source === 'leave' ? 'Approved leave' : 'Slack'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAttendance;
